import { isNil } from 'lodash';
import { useEffect } from 'react';
import {
  FieldPath,
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { Radio } from '@mantine/core';
import { ISelectOption } from '../../types';
import ControlledInputLabelComponent from './ControlledInputLabelComponent';
import FieldErrorMessage from './FieldErrorMessage';

export interface ControlledToggleInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends UseControllerProps<TFieldValues, TName> {
  label?: string;
  trueOption: ISelectOption;
  falseOption: ISelectOption;
}

const ControlledToggleInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  label,
  defaultValue,
  trueOption,
  falseOption,
  shouldUnregister = true,
  ...controllerProps
}: ControlledToggleInputProps<TFieldValues, TName>) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController<TFieldValues, TName>({
    ...controllerProps,
    shouldUnregister,
  });

  useEffect(() => {
    if (isNil(value)) {
      onChange('false');
    }
  }, [onChange, value]);

  const isTrueOptionSelected =
    value?.toString() === trueOption.value?.toString();

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        {!!label && (
          <div className='flex-1'>
            <ControlledInputLabelComponent label={label} />
          </div>
        )}
        <Radio.Group
          value={value}
          onChange={() => {
            if (isTrueOptionSelected) {
              onChange(falseOption.value.toString());
            } else {
              onChange(trueOption.value.toString());
            }
          }}
          className='flex flex-row gap-6'
        >
          <Radio value={trueOption.value.toString()} label={trueOption.label} />
          <Radio
            value={falseOption.value.toString()}
            label={falseOption.label}
          />
        </Radio.Group>
      </div>
      <FieldErrorMessage message={error?.message} />
    </div>
  );
};

export default ControlledToggleInput;
